/**
 * 随机事件池（数据驱动）
 *
 * 设计纪律：
 * 1. 事件只削减进度、不造成失败 —— 危害事件是压力，不是惩罚（GDD 4.3）。
 * 2. 抽取完全由 `eventSeed` 派生，可复算，服务端能校验客户端上报的对局。
 * 3. 每个事件的触发条件都是纯函数式判定（when.minMist / when.minTurn），
 *    禁止出现 `Math.random`、`Date.now`。
 *
 * 关卡通过 `LevelConfig.eventPoolIds` 组合自己的池子；配平数值见
 * `docs/传灯-第一章关卡设计表.md` 第 5 节。
 */

import type { RandomEventDef } from "../types.js";
import type { Rng } from "../rng.js";

/** 全部事件定义（按 id 索引） */
export const ALL_EVENTS: readonly RandomEventDef[] = [
  // ---------- 增益 ----------
  {
    id: "ev-traveling-scholar",
    kind: "randomEvent",
    title: "游学学者",
    message: "一位游学学者带来了远方的手稿，智慧之光更盛。",
    weight: 24,
    when: { minTurn: 2 },
    effects: [{ kind: "wisdom", value: 8 }],
  },
  {
    id: "ev-merchant",
    kind: "randomEvent",
    title: "商队来访",
    message: "商队带来了异乡的消息，一处营地被重新点燃。",
    weight: 22,
    effects: [
      { kind: "enlightenment", value: 5, target: "randomLit" },
      { kind: "wisdom", value: 4 },
    ],
  },
  {
    id: "ev-bumper-harvest",
    kind: "randomEvent",
    title: "丰年",
    message: "这一年风调雨顺，灯火燃得更旺了。",
    weight: 20,
    effects: [{ kind: "energy", value: 10 }],
  },
  {
    id: "ev-ancient-book",
    kind: "randomEvent",
    title: "偶得古籍",
    message: "在旧书堆里翻到一册残卷，字里行间都是前人的经验。",
    weight: 16,
    when: { minTurn: 2 },
    effects: [{ kind: "wisdom", value: 12 }],
  },

  // ---------- 危害 ----------
  {
    id: "ev-rumor",
    kind: "randomEvent",
    title: "谣言四起",
    message: "不知从何而来的谣言动摇了一处营地，灯光黯淡了些。",
    weight: 22,
    when: { minTurn: 4 },
    effects: [{ kind: "enlightenment", value: -3, target: "randomLit" }],
  },
  {
    id: "ev-mist-surge",
    kind: "mistSpread",
    title: "迷雾反扑",
    message: "迷雾在夜色里聚拢，吞噬了一小片刚刚亮起的土地。",
    weight: 30,
    when: { minMist: 30 },
    effects: [
      { kind: "mist", value: 5 },
      { kind: "enlightenment", value: -3, target: "randomDark" },
    ],
  },
  {
    id: "ev-close-route",
    kind: "randomEvent",
    title: "商路断绝",
    message: "前方传来变故的消息，一条商路暂时无法通行。",
    weight: 18,
    when: { minTurn: 6 },
    effects: [{ kind: "closeRoute", value: 1 }],
  },
  {
    id: "ev-plague",
    kind: "mistSpread",
    title: "疫病蔓延",
    message: "一场疫病让村落闭门不出，传播的脚步慢了下来。",
    weight: 16,
    when: { minMist: 40 },
    effects: [
      { kind: "mist", value: 4 },
      { kind: "enlightenment", value: -4, target: "randomDark" },
    ],
  },
  {
    id: "ev-disaster",
    kind: "mistSpread",
    title: "天灾",
    message: "天灾阻断了来往，火种的能量被消耗了一些。",
    weight: 15,
    when: { minTurn: 8 },
    effects: [
      { kind: "mist", value: 6 },
      { kind: "energy", value: -5 },
    ],
  },
];

/** 危害事件 id（未指定事件池时的兜底池，等价于骨架版本的"迷雾反噬"行为） */
const HAZARD_EVENT_IDS = [
  "ev-rumor",
  "ev-mist-surge",
  "ev-close-route",
  "ev-plague",
  "ev-disaster",
] as const;

/** 未指定事件池时的默认池：只含危害事件（增益事件必须由关卡显式开启） */
export const DEFAULT_EVENT_POOL: readonly RandomEventDef[] = HAZARD_EVENT_IDS.map(
  (id) => {
    const found = ALL_EVENTS.find((e) => e.id === id);
    if (!found) throw new Error(`默认事件池引用了未知事件: ${id}`);
    return found;
  },
);

/** 按 id 取事件定义 */
export function getEventById(id: string): RandomEventDef | undefined {
  return ALL_EVENTS.find((e) => e.id === id);
}

/** 把关卡配置的 id 列表解析成事件池（未知 id 直接抛错，早失败好过静默降级） */
export function resolveEventPool(ids: readonly string[]): RandomEventDef[] {
  return ids.map((id) => {
    const found = getEventById(id);
    if (!found) throw new Error(`未知的事件 id: ${id}`);
    return found;
  });
}

export interface EventDrawContext {
  mist: number;
  turn: number;
}

/**
 * 按权重从事件池里确定性抽取一个满足条件的事件。
 * 注意：只要池子里有候选，就会消耗一次 RNG（权重轮盘），
 * 这是"同种子同轨迹"的一部分，不要随意调整调用顺序。
 */
export function drawEvent(
  rng: Rng,
  pool: readonly RandomEventDef[],
  ctx: EventDrawContext,
): RandomEventDef | null {
  const eligible = pool.filter(
    (e) =>
      (e.when?.minMist === undefined || ctx.mist >= e.when.minMist) &&
      (e.when?.minTurn === undefined || ctx.turn >= e.when.minTurn) &&
      (e.when?.maxTurn === undefined || ctx.turn <= e.when.maxTurn),
  );
  if (eligible.length === 0) return null;

  const total = eligible.reduce((sum, e) => sum + e.weight, 0);
  if (total <= 0) return null;

  let roll = rng() * total;
  for (const e of eligible) {
    roll -= e.weight;
    if (roll < 0) return e;
  }
  return eligible[eligible.length - 1] ?? null;
}
